import React from "react";
import { Reveal } from "./Reveal";
import { STEPS } from "../../data/content";
import CtaButton from "./CtaButton";

const DOT = {
  bright: "bg-emerald-300 shadow-[0_0_8px_rgba(110,231,183,0.7)]",
  green: "bg-green-500",
  amber: "bg-amber-400",
  red: "bg-red-400",
};

const Tutorial = () => {
  return (
    <section id="tutorial" data-testid="tutorial-section" className="py-24 md:py-32 border-t border-white/10 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Sticky intro */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-5">
                  / Step-by-step
                </p>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
                  From sign-in to your first roll.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-zinc-400 leading-relaxed">
                  Seven steps to go from a fresh login to reviewing your full
                  rolling history — including how to read the color-coded roll
                  candidates. Follow along here, then open the terminal and do it
                  for real.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-8">
                  <CtaButton ctaId="tutorial-open-app-cta" label="Open ROLL.OPS" location="tutorial">
                    Open ROLL.OPS
                  </CtaButton>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Steps */}
          <div className="lg:col-span-8 flex flex-col gap-16 md:gap-24">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={0.05}>
                <div data-testid={`tutorial-step-${i + 1}`}>
                  <div className="flex items-baseline gap-5">
                    <span className="font-mono font-light text-5xl md:text-6xl text-white/15 leading-none select-none">
                      {s.num}
                    </span>
                    <div className="flex-1 pt-1">
                      <h3 className="font-display text-2xl font-medium tracking-tight text-white">
                        {s.title}
                      </h3>
                      <p className="mt-3 text-zinc-400 leading-relaxed max-w-xl">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                  {s.wideImages ? (
                    <div className="mt-7 ml-0 md:ml-[68px] space-y-6">
                      {s.wideImages.map((im, idx) => (
                        <figure key={idx} className="border border-white/10 bg-[#0A0A0A]">
                          <div className="flex items-center gap-1.5 px-3 h-8 border-b border-white/10 bg-black/40">
                            <span className="h-2 w-2 rounded-full bg-white/15" />
                            <span className="h-2 w-2 rounded-full bg-white/15" />
                            <span className="h-2 w-2 rounded-full bg-white/15" />
                            <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-600">
                              step {s.num}.{idx + 1}
                            </span>
                          </div>
                          <img src={im.src} alt={im.caption} className="w-full block" loading="lazy" />
                          <figcaption className="px-4 py-3 border-t border-white/10 font-mono text-[11px] leading-snug text-zinc-500">
                            {im.caption}
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  ) : s.images ? (
                    <div className="mt-7 ml-0 md:ml-[68px] grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {s.images.map((im, idx) => (
                        <figure key={idx} className="border border-white/10 bg-[#0A0A0A]">
                          <div className="flex items-center gap-1.5 px-3 h-8 border-b border-white/10 bg-black/40">
                            <span className="h-2 w-2 rounded-full bg-white/15" />
                            <span className="h-2 w-2 rounded-full bg-white/15" />
                            <span className="h-2 w-2 rounded-full bg-white/15" />
                            <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-600">
                              step {s.num}.{idx + 1}
                            </span>
                          </div>
                          <div className="aspect-[3/4] bg-[#050505] flex items-center justify-center overflow-hidden">
                            <img
                              src={im.src}
                              alt={im.caption}
                              className="max-w-full max-h-full object-contain"
                              loading="lazy"
                            />
                          </div>
                          <figcaption className="px-3 py-3 border-t border-white/10 font-mono text-[11px] leading-snug text-zinc-500">
                            {im.caption}
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  ) : s.image ? (
                    <div className="mt-7 ml-0 md:ml-[68px] border border-white/10 bg-[#0A0A0A]">
                      <div className="flex items-center gap-1.5 px-3 h-8 border-b border-white/10 bg-black/40">
                        <span className="h-2 w-2 rounded-full bg-white/15" />
                        <span className="h-2 w-2 rounded-full bg-white/15" />
                        <span className="h-2 w-2 rounded-full bg-white/15" />
                        <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-600">
                          step {s.num}{s.wide ? " · live terminal" : ""}
                        </span>
                      </div>
                      {s.wide ? (
                        <img
                          src={s.image}
                          alt={s.title}
                          className="w-full block"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className={`${
                            s.portrait ? "aspect-[3/4] max-w-sm mx-auto" : "aspect-[16/10]"
                          } bg-[#050505] flex items-center justify-center overflow-hidden`}
                        >
                          <img
                            src={s.image}
                            alt={s.title}
                            className="max-w-full max-h-full object-contain"
                            loading="lazy"
                          />
                        </div>
                      )}
                    </div>
                  ) : null}

                  {s.colorGuide && (
                    <div className="mt-5 ml-0 md:ml-[68px] grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {s.colorGuide.map((group) => (
                        <div
                          key={group.title}
                          className="border border-white/10 bg-[#0A0A0A] p-5"
                        >
                          <p className="font-mono text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">
                            {group.title}
                          </p>
                          <div className="space-y-2.5">
                            {group.items.map((it, gi) => (
                              <div key={gi} className="flex items-start gap-2.5">
                                <span
                                  className={`mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0 ${DOT[it.color]}`}
                                />
                                <p className="text-sm text-zinc-400 leading-snug">
                                  {it.text}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {s.legend && (
                    <div className="mt-5 ml-0 md:ml-[68px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {s.legend.map((l) => (
                        <div
                          key={l.label}
                          data-testid={`legend-${l.color}`}
                          className="border border-white/10 bg-[#0A0A0A] p-4"
                        >
                          <div className="flex items-center gap-2">
                            <span className={`h-2.5 w-2.5 rounded-full ${DOT[l.color]}`} />
                            <span className="font-mono text-xs uppercase tracking-[0.16em] text-white">
                              {l.label}
                            </span>
                          </div>
                          <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                            {l.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {s.points && (
                    <div className="mt-4 ml-0 md:ml-[68px] grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 border-t border-white/10 pt-5">
                      {s.points.map((p) => (
                        <div key={p.k} className="flex gap-3">
                          <span className="mt-[7px] h-1.5 w-1.5 bg-cyan-400 flex-shrink-0" />
                          <p className="text-sm text-zinc-400 leading-relaxed">
                            <span className="text-white font-medium">{p.k}</span> — {p.v}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Tutorial;
